// ============================================================================
// useSpecialismOptions.js — Supabase CRUD for the specialism_options table
// ============================================================================
// Category-specific specialism tags for the Contacts Directory.
// Each option belongs to a vendor category (Vendor / Supplier, Manufacturer,
// Contractor, Consultants). Supports fetching + adding new options.
// ============================================================================

import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabaseClient';

/** Capitalize each word: "plumbing & piping" → "Plumbing & Piping" */
function capitalizeWords(str) {
  return str.replace(/\b[a-z]/g, ch => ch.toUpperCase());
}

export function useSpecialismOptions() {
  const [allOptions, setAllOptions] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchOptions = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('specialism_options')
        .select('id, label, category')
        .order('label', { ascending: true });

      if (error) {
        console.warn('[useSpecialismOptions] fetch error:', error.message);
        setAllOptions([]);
      } else {
        setAllOptions(data || []);
      }
    } catch (err) {
      console.warn('[useSpecialismOptions] fetch error:', err.message);
      setAllOptions([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchOptions(); }, [fetchOptions]);

  /**
   * Get options filtered by category. Returns array of label strings.
   * If category is empty/null, returns all options.
   */
  const getOptionsForCategory = useCallback((category) => {
    if (!category) return allOptions.map(o => o.label);
    return allOptions.filter(o => o.category === category).map(o => o.label);
  }, [allOptions]);

  /**
   * Add a new specialism option.
   * Returns { success } or { success: false, error }.
   */
  const addOption = async (label, category) => {
    const normalized = capitalizeWords((label || '').trim());
    if (!normalized) return { success: false, error: 'Empty value' };
    if (!category) return { success: false, error: 'Select a category first' };

    // Duplicate check (case-insensitive, within category)
    const existing = allOptions.find(
      o => o.category === category && o.label.toLowerCase() === normalized.toLowerCase()
    );
    if (existing) return { success: false, error: 'Option already exists', existingLabel: existing.label };

    try {
      const { data: session } = await supabase.auth.getSession();
      if (!session?.session?.user) throw new Error('Please sign in');

      const { data, error } = await supabase
        .from('specialism_options')
        .insert([{ label: normalized, category }])
        .select('id, label, category')
        .single();
      if (error) throw error;

      setAllOptions(prev => [...prev, data].sort((a, b) => a.label.localeCompare(b.label)));
      return { success: true, option: data };
    } catch (err) {
      return { success: false, error: err.message };
    }
  };

  const removeOption = async (label, category) => {
    const trimmed = (label || '').trim();
    if (!trimmed) return { success: false, error: 'Empty value' };

    const match = allOptions.find(
      o => o.category === category && o.label.toLowerCase() === trimmed.toLowerCase()
    );
    if (!match) return { success: false, error: 'Option not found' };

    try {
      const { data: session } = await supabase.auth.getSession();
      if (!session?.session?.user) throw new Error('Please sign in');

      const { error } = await supabase
        .from('specialism_options')
        .delete()
        .eq('id', match.id);
      if (error) throw error;

      setAllOptions(prev => prev.filter(o => o.id !== match.id));
      return { success: true };
    } catch (err) {
      return { success: false, error: err.message };
    }
  };

  const updateOption = async (id, newLabel) => {
    const trimmed = capitalizeWords((newLabel || '').trim());
    if (!trimmed) return { success: false, error: 'Empty value' };

    try {
      const { data: session } = await supabase.auth.getSession();
      if (!session?.session?.user) throw new Error('Please sign in');

      const { error } = await supabase
        .from('specialism_options')
        .update({ label: trimmed })
        .eq('id', id);
      if (error) throw error;

      setAllOptions(prev =>
        prev.map(o => o.id === id ? { ...o, label: trimmed } : o)
          .sort((a, b) => a.label.localeCompare(b.label))
      );
      return { success: true };
    } catch (err) {
      return { success: false, error: err.message };
    }
  };

  return { allOptions, loading, getOptionsForCategory, addOption, updateOption, removeOption, refetch: fetchOptions };
}
